import { useEffect, useState } from "react";
import axiosInstance from "config/axiosInstance";
import { Card, LinearProgress } from "@mui/material";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import { IoCalendar, IoTrendingUp, IoStatsChart } from "react-icons/io5";

function WeeklyPrediction() {
  const [loading, setLoading] = useState(true);
  const [prediction, setPrediction] = useState(null);

  useEffect(() => {
    const fetchPrediction = async () => {
      try {
        const res = await axiosInstance.get("/analytics/prediccion");
        
        if (res.data?.success && res.data.prediccion) {
          setPrediction(res.data.prediccion);
        }
        
        setLoading(false);
      } catch (err) {
        setLoading(false);
      }
    };

    fetchPrediction();
  }, []);

  if (loading) {
    return (
      <Card sx={{ height: "100%", p: 3 }}>
        <VuiTypography variant="lg" color="white" fontWeight="bold">
          Cargando predicción...
        </VuiTypography>
      </Card>
    );
  }

  if (!prediction) {
    return (
      <Card sx={{ height: "100%", p: 3 }}>
        <VuiTypography variant="lg" color="white" fontWeight="bold" mb={2}>
          Predicción Semanal
        </VuiTypography>
        <VuiTypography variant="button" color="text">
          No hay suficientes datos históricos para generar una predicción.
        </VuiTypography>
      </Card>
    );
  }

  const getConfidenceColor = (confidence) => {
    if (confidence >= 70) return "#0DDA88";
    if (confidence >= 50) return "#FFA500";
    return "#FF3838";
  };

  const getConfidenceLabel = (confidence) => {
    if (confidence >= 70) return "Alta";
    if (confidence >= 50) return "Media";
    return "Baja";
  };

  return (
    <Card sx={{ height: "100%", p: 3 }}>
      <VuiBox mb={3}>
        <VuiBox display="flex" alignItems="center" gap={1} mb={1}>
          <IoCalendar size={24} color="#0075FF" />
          <VuiTypography variant="lg" color="white" fontWeight="bold">
            Predicción Semanal
          </VuiTypography>
        </VuiBox>
        <VuiTypography variant="button" color="text">
          Proyección basada en las últimas 4 semanas
        </VuiTypography>
      </VuiBox>

      {/* Main prediction card */}
      <VuiBox
        sx={{
          background: "linear-gradient(127.09deg, rgba(0, 117, 255, 0.2) 0%, rgba(0, 117, 255, 0.05) 100%)",
          borderRadius: "12px",
          p: 3,
          mb: 3
        }}
      >
        <VuiBox display="flex" alignItems="center" gap={2} mb={2}>
          <IoTrendingUp size={32} color="#0075FF" />
          <VuiBox>
            <VuiTypography variant="caption" color="text">
              Monto estimado próxima semana
            </VuiTypography>
            <VuiTypography variant="h3" color="white" fontWeight="bold">
              S/ {prediction.montoEstimado.toFixed(2)}
            </VuiTypography>
          </VuiBox>
        </VuiBox>

        <VuiBox>
          <VuiTypography variant="caption" color="text">
            Cantidad estimada de depósitos
          </VuiTypography>
          <VuiTypography variant="h4" color="white" fontWeight="bold">
            {prediction.cantidadEstimada} depósitos
          </VuiTypography>
        </VuiBox>
      </VuiBox>

      {/* Confidence indicator */}
      <VuiBox>
        <VuiBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <VuiBox display="flex" alignItems="center" gap={1}>
            <IoStatsChart size={16} color={getConfidenceColor(prediction.confianza)} />
            <VuiTypography variant="button" color="text">
              Nivel de confianza
            </VuiTypography>
          </VuiBox>
          <VuiTypography
            variant="button"
            fontWeight="bold"
            sx={{ color: getConfidenceColor(prediction.confianza) }}
          >
            {getConfidenceLabel(prediction.confianza)} ({prediction.confianza}%)
          </VuiTypography>
        </VuiBox>
        
        <LinearProgress
          variant="determinate"
          value={prediction.confianza}
          sx={{
            height: 8,
            borderRadius: 4,
            backgroundColor: "rgba(255,255,255,0.1)",
            "& .MuiLinearProgress-bar": {
              borderRadius: 4,
              background: `linear-gradient(90deg, ${getConfidenceColor(prediction.confianza)} 0%, ${getConfidenceColor(prediction.confianza)}88 100%)`
            }
          }}
        />
      </VuiBox>

      {/* Info box */}
      <VuiBox
        mt={3}
        sx={{
          background: "rgba(255,255,255,0.05)",
          borderRadius: "8px",
          p: 2
        }}
      >
        <VuiTypography variant="caption" color="text">
          💡 <strong>Nota:</strong> Esta predicción se basa en el análisis de los patrones de depósitos de las últimas 4 semanas. 
          {prediction.confianza < 50 && " Se recomienda recopilar más datos para mejorar la precisión."}
        </VuiTypography>
      </VuiBox>
    </Card>
  );
}

export default WeeklyPrediction;
